import React from 'react';
import { Navigate } from 'react-router-dom';

// Pages
import Login from '@/pages/auth/Login';
import SsoLogin from '@/pages/auth/SsoLogin';
import MainLayout from '@/layouts/MainLayout';  

// Learn Pages
import {
  OnlineCourseList,
  OnlineCourseDetail,
  InteractiveLearningList,
  InteractiveLearningDetail,
  RecommendationList,
  RecommendationDetail,
  PracticeList,
  PracticeDetail
} from '@/pages/learn';
import AssignedPracticeList from '@/pages/learn/AssignedPracticeList';
import ScenarioDetail from '@/pages/learn/ScenarioDetail';
import ScenarioSessions from '@/pages/learn/ScenarioSessions';
import SessionDetail from '@/pages/learn/SessionDetail';

// Settings Pages
import LanguageSettings from '@/pages/my/subPage/LanguageSettings';
import ServiceAgreement from '@/pages/my/subPage/ServiceAgreement';
import PrivacyPolicy from '@/pages/my/subPage/PrivacyPolicy';
import MyProfile from '@/pages/my/subPage/MyProfile';
import Feedback from '@/pages/my/subPage/Feedback';
import AccountSecurity from '@/pages/my/subPage/AccountSecurity';
import AboutUs from '@/pages/my/subPage/AboutUs';
import DebugStorage from '@/pages/my/subPage/DebugStorage';

// Index Pages
import IndexTaskPage from '../pages/index-pages/index-task/IndexTaskPage';


// AI陪练的页面
import AiTrainingList from '../pages/ai-training/list/AiTrainingList';
import AiTrainingDetail from '../pages/ai-training/detail/AiTrainingDetail';
import AiTrainingRankList from '../pages/ai-training/rank/rankList';
import AiTrainingHistory from '../pages/ai-training/history/AiTrainingHistory';
import AiTrainingAnalysis from '../pages/ai-training/analysis/AiTrainingAnalysis';
import AiTrainingChat from '../pages/ai-training/chat/AiTrainingChat';
import AiTrainingPreview from '@/pages/ai-training/preview/AiTrainingPreview';
import AiTrainingDebug from '@/pages/ai-training/debug/aiTrainingDebug';
import ScenarioDebug from '@/pages/ai-training/debug/ScenarioDebug';
// AI督导的页面
import AiSupervisorList from '../pages/ai-supervisor/list/AiSupervisorList';
import SupervisorDetail from '../pages/ai-supervisor/detail/SupervisorDetail';
import AiSupervisorRecords from '../pages/ai-supervisor/records/AiSupervisorRecords';
import SupervisorUpload from '../pages/ai-supervisor/upload/SupervisorUpload';
import AiSupervisorAnalysis from '../pages/ai-supervisor/analysis/AiSupervisorAnalysis';
import RankList from '../pages/ai-supervisor/rank-list/RankList';
// 我的页面
import MySetting from '../pages/my/MySetting';

import DingTalkLogin from '../pages/auth/DingTalkLogin';


// 路由类型定义
export interface RouteConfig {
  path: string;
  element: React.ReactNode;
  name?: string; // 路由名称，用于消息通知等场景
  meta?: {
    title?: string; // 页面标题
    description?: string; // 页面描述
  };
  children?: RouteConfig[];
}

// 路由配置
const routes: RouteConfig[] = [
  {
    path: '/',
    element: <MainLayout />,
    name: 'MainLayout',
    // 需要保护的路由作为子路由嵌套在MainLayout中
    children: [
      // { path: '', element: <Home />, name: 'Home' },
      { path: '', element: <Navigate to="/ai-training/list" replace /> },

      // tab页的路由
      { path: 'index-task', element: <IndexTaskPage />, name: 'IndexTask', meta: { title: '任务中心' } },
      { path: 'my-setting', element: <MySetting />, name: 'MySetting', meta: { title: '我的设置' } },

      // AI陪练的页面
      { path: 'ai-training/list', element: <AiTrainingList />, name: 'AiTrainingList', meta: { title: 'AI陪练列表' } },
      { path: 'ai-training/detail/:id', element: <AiTrainingDetail />, name: 'AiTrainingDetail', meta: { title: 'AI陪练详情' } },
      { path: 'ai-training/rank/list/:scenarioId', element: <AiTrainingRankList />, name: 'AiTrainingRankList', meta: { title: 'AI陪练排行榜' } },
      { path: 'ai-training/history/:scenarioId', element: <AiTrainingHistory />, name: 'AiTrainingHistory', meta: { title: 'AI陪练历史' } },
      { path: 'ai-training/analysis/:sessionId', element: <AiTrainingAnalysis />, name: 'AiTrainingAnalysis', meta: { title: 'AI陪练分析' } },
      { path: 'ai-training/chat/:sessionId', element: <AiTrainingChat />, name: 'AiTrainingChat', meta: { title: 'AI陪练对话' } },
      { path: 'ai-training/preview/:scenarioId', element: <AiTrainingPreview />, name: 'AiTrainingPreview', meta: { title: 'AI陪练预览' } },
      { path: 'ai-training/debug', element: <AiTrainingDebug />, name: 'AiTrainingDebug', meta: { title: 'AI陪练调试' } },
      { path: 'ai-training/scenario-debug', element: <ScenarioDebug />, name: 'ScenarioDebug', meta: { title: '场景调试' } },

      // AI督导的页面
      { path: 'ai-supervisor/list', element: <AiSupervisorList />, name: 'AiSupervisorList', meta: { title: 'AI督导场景' } },
      { path: 'ai-supervisor/detail/:scenarioId', element: <SupervisorDetail />, name: 'SupervisorDetail', meta: { title: '督导详情' } },
      { path: 'ai-supervisor/records/:scenarioId', element: <AiSupervisorRecords />, name: 'AiSupervisorRecords', meta: { title: '分析记录' } },
      { path: 'ai-supervisor/upload/:scenarioId', element: <SupervisorUpload />, name: 'SupervisorUpload', meta: { title: '上传音频' } },
      { path: 'ai-supervisor/analysis', element: <AiSupervisorAnalysis />, name: 'AiSupervisorAnalysis', meta: { title: 'AI督导分析' } },
      { path: 'ai-supervisor/rank-list/:scenarioId', element: <RankList />, name: 'RankList', meta: { title: '排行榜' } },

      // Learn module routes
      { path: 'learn/courses', element: <OnlineCourseList />, name: 'OnlineCourseList', meta: { title: '在线课程' } },
      { path: 'learn/courses/:id', element: <OnlineCourseDetail />, name: 'OnlineCourseDetail', meta: { title: '课程详情' } },
      { path: 'learn/interactive', element: <InteractiveLearningList />, name: 'InteractiveLearningList', meta: { title: '互动学习' } },
      { path: 'learn/interactive/:id', element: <InteractiveLearningDetail />, name: 'InteractiveLearningDetail', meta: { title: '互动学习详情' } },
      { path: 'learn/recommendations', element: <RecommendationList />, name: 'RecommendationList', meta: { title: '推荐内容' } },
      { path: 'learn/recommendations/:id', element: <RecommendationDetail />, name: 'RecommendationDetail', meta: { title: '推荐详情' } },
      { path: 'learn/practice', element: <PracticeList />, name: 'PracticeList', meta: { title: '练习列表' } },
      { path: 'learn/practice/:id', element: <PracticeDetail />, name: 'PracticeDetail', meta: { title: '练习详情' } },
      { path: 'learn/assigned-practice', element: <AssignedPracticeList />, name: 'AssignedPracticeList', meta: { title: '指定练习' } },
      { path: 'learn/scenario/:id', element: <ScenarioDetail />, name: 'ScenarioDetail', meta: { title: '场景详情' } },
      { path: 'learn/scenario/:scenarioId/sessions', element: <ScenarioSessions />, name: 'ScenarioSessions', meta: { title: '场景会话' } },
      { path: 'learn/session/:sessionId', element: <SessionDetail />, name: 'SessionDetail', meta: { title: '会话详情' } },

      // Settings module routes
      { path: 'settings/language', element: <LanguageSettings />, name: 'LanguageSettings', meta: { title: '语言设置' } },
      { path: 'settings/service-agreement', element: <ServiceAgreement />, name: 'ServiceAgreement', meta: { title: '服务协议' } },
      { path: 'settings/privacy-policy', element: <PrivacyPolicy />, name: 'PrivacyPolicy', meta: { title: '隐私政策' } },
      { path: 'settings/my-profile', element: <MyProfile />, name: 'MyProfile', meta: { title: '我的资料' } },
      { path: 'settings/feedback', element: <Feedback />, name: 'Feedback', meta: { title: '意见反馈' } },
      { path: 'settings/account-security', element: <AccountSecurity />, name: 'AccountSecurity', meta: { title: '账号安全' } },
      { path: 'settings/about-us', element: <AboutUs />, name: 'AboutUs', meta: { title: '关于我们' } },
      { path: 'settings/debug-storage', element: <DebugStorage />, name: 'DebugStorage', meta: { title: '存储调试' } },

      { path: '*', element: <Navigate to="/" replace /> }
    ],
  },
  {
    path: 'login',
    element: <Login />,
    name: 'Login',
    meta: { title: '登录' }
  },
  // routes/index.tsx
{
  path: '/dingtalk-login',
  element: <DingTalkLogin />,
  name: 'DingTalkLogin',
  meta: { title: '钉钉登录' }
},
  {
    path: 'ssologin',
    element: <SsoLogin />,
    name: 'SsoLogin',
    meta: { title: 'SSO登录' }
  }
];

// 路由名称到路径的映射表
const routeNameMap: Record<string, string> = {};

// 递归构建路由名称映射表
const buildRouteNameMap = (routes: RouteConfig[], parentPath = '') => {
  routes.forEach(route => {
    const fullPath = parentPath + '/' + route.path;
    const normalizedPath = fullPath.replace(/\/+/g, '/'); // 规范化路径，去除多余的斜杠
    
    if (route.name) {
      routeNameMap[route.name] = normalizedPath;
    }
    
    if (route.children) {
      buildRouteNameMap(route.children, normalizedPath);
    }
  });
};

// 初始化路由名称映射表
buildRouteNameMap(routes);

/**
 * 根据路由名称获取路径
 * @param name 路由名称
 * @returns 路径
 */
export const getPathByName = (name: string): string | undefined => {
  return routeNameMap[name];
};

/**
 * 根据路由名称和参数生成完整路径
 * @param name 路由名称
 * @param params 路径参数，如 { id: '123', scenarioId: '456' }
 * @returns 完整路径
 * 
 * @example
 * generatePath('AiTrainingDetail', { id: '123' }) // 返回 '/ai-training/detail/123'
 * generatePath('AiTrainingRankList', { scenarioId: '456' }) // 返回 '/ai-training/rank/list/456'
 */
export const generatePath = (name: string, params?: Record<string, string | number>): string => {
  let path = routeNameMap[name];
  
  if (!path) {
    console.warn(`路由名称 "${name}" 未找到`);
    return '/';
  }
  
  // 替换路径参数
  if (params) {
    Object.keys(params).forEach(key => {
      path = path.replace(`:${key}`, String(params[key]));
    });
  }
  
  return path;
};

/**
 * 导出路由名称常量，方便使用
 */
export const RouteNames = {
  // Tab页面
  IndexTask: 'IndexTask',
  MySetting: 'MySetting',

  // AI陪练
  AiTrainingList: 'AiTrainingList',
  AiTrainingDetail: 'AiTrainingDetail',
  AiTrainingRankList: 'AiTrainingRankList',
  AiTrainingHistory: 'AiTrainingHistory',
  AiTrainingAnalysis: 'AiTrainingAnalysis',
  AiTrainingChat: 'AiTrainingChat',
  AiTrainingPreview: 'AiTrainingPreview',
  AiTrainingDebug: 'AiTrainingDebug',
  ScenarioDebug: 'ScenarioDebug',

  // AI督导
  AiSupervisorList: 'AiSupervisorList',
  SupervisorDetail: 'SupervisorDetail',
  AiSupervisorRecords: 'AiSupervisorRecords',
  SupervisorUpload: 'SupervisorUpload',
  AiSupervisorAnalysis: 'AiSupervisorAnalysis',
  RankList: 'RankList',

  // 学习模块
  OnlineCourseList: 'OnlineCourseList',
  OnlineCourseDetail: 'OnlineCourseDetail',
  InteractiveLearningList: 'InteractiveLearningList',
  InteractiveLearningDetail: 'InteractiveLearningDetail',
  RecommendationList: 'RecommendationList',
  RecommendationDetail: 'RecommendationDetail',
  PracticeList: 'PracticeList',
  PracticeDetail: 'PracticeDetail',
  AssignedPracticeList: 'AssignedPracticeList',
  ScenarioDetail: 'ScenarioDetail',
  ScenarioSessions: 'ScenarioSessions',
  SessionDetail: 'SessionDetail',

  // 设置模块
  LanguageSettings: 'LanguageSettings',
  ServiceAgreement: 'ServiceAgreement',
  PrivacyPolicy: 'PrivacyPolicy',
  MyProfile: 'MyProfile',
  Feedback: 'Feedback',
  AccountSecurity: 'AccountSecurity',
  AboutUs: 'AboutUs',
  DebugStorage: 'DebugStorage',

  // 认证
  Login: 'Login',
  SsoLogin: 'SsoLogin',
} as const;

export default routes;